import { useSEO } from '../lib/seo';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowUpRight, Check, MapPin } from '@phosphor-icons/react';
import { PageHero, Reveal, Chapter } from '../lib/motion';
import { CATEGORIES, COUNTRIES, VERTICALS, SECTORS, FEATURED_SLUGS } from '../data/site';

const chipCls =
  'inline-flex items-center gap-2 border border-line px-4 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-steel transition-colors duration-200 hover:border-volt/60 hover:text-volt';

const ALL_PAGES = [...CATEGORIES, ...VERTICALS, ...SECTORS];

const CategoryPage = () => {
  const { slug, countryCode } = useParams();
  const cat = ALL_PAGES.find((c) => c.slug === slug);
  const country = countryCode ? COUNTRIES.find((c) => c.code === countryCode.toUpperCase()) : null;
  // Only featured trades, verticals and sector hubs have per-market pages; a country
  // URL on any other page redirects to its global version.
  const hasCountryPages = cat ? !!(cat.vertical || cat.sector) || FEATURED_SLUGS.includes(cat.slug) : false;
  const badCountry = countryCode && (!country || !hasCountryPages);
  const invalid = !cat;
  // SG country page renders the original Singapore-targeted copy verbatim (SEO moat);
  // every other market page renders the global copy plus a country profile block.
  const content = cat && country?.code === 'SG' && cat.sg ? { ...cat, ...cat.sg } : cat;
  const path = cat ? `/services/${cat.slug}${country && hasCountryPages ? `/${country.code.toLowerCase()}` : ''}` : '/';
  const effectiveCountry = hasCountryPages ? country : null;

  const seoName = cat?.shortName || cat?.name;
  const seoCountryTitle = effectiveCountry ? effectiveCountry.seoName.replace(/^the /, '') : '';
  const pageTitle = invalid
    ? 'Vox Meridian'
    : effectiveCountry
      ? `${seoName} in ${seoCountryTitle} | Vox Meridian`
      : `${seoName} | Vox Meridian`;
  const pageDescription = invalid
    ? ''
    : effectiveCountry
      ? `${content.tagline} Post one RFQ and get matched to verified providers in ${effectiveCountry.name}.`
      : `${cat.tagline} Post one RFQ and get matched to verified providers in 10 markets.`;
  const pageImage = content?.image || cat?.image;
  const pageJsonLd = invalid
    ? null
    : [
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: pageTitle,
          description: pageDescription,
          url: `https://voxmeridian.com${path}`,
          isPartOf: { '@type': 'WebSite', name: 'Vox Meridian', url: 'https://voxmeridian.com' },
          about: {
            '@type': 'Service',
            serviceType: cat.name,
            areaServed: effectiveCountry
              ? { '@type': 'Place', name: effectiveCountry.name }
              : COUNTRIES.map((c) => ({ '@type': 'Place', name: c.name })),
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://voxmeridian.com/' },
            { '@type': 'ListItem', position: 2, name: 'Services', item: 'https://voxmeridian.com/services' },
            { '@type': 'ListItem', position: 3, name: pageTitle, item: `https://voxmeridian.com${path}` },
          ],
        },
      ];

  useSEO({ title: pageTitle, description: pageDescription, path, image: pageImage, jsonLd: pageJsonLd });

  if (invalid) return <Navigate to="/" replace />;
  if (badCountry) return <Navigate to={`/services/${cat.slug}`} replace />;
  const siblings = cat.vertical ? VERTICALS : cat.sector ? SECTORS : CATEGORIES;
  const idx = siblings.indexOf(cat);
  const next = siblings[(idx + 1) % siblings.length];
  const nextMarkets = next.vertical || next.sector || FEATURED_SLUGS.includes(next.slug);
  const nextTo = `/services/${next.slug}${effectiveCountry && nextMarkets ? `/${effectiveCountry.code.toLowerCase()}` : ''}`;
  const rfqTo = cat.vertical || cat.sector
    ? `/buyers${effectiveCountry ? `?country=${effectiveCountry.code}` : ''}`
    : `/buyers?category=${cat.slug}${effectiveCountry ? `&country=${effectiveCountry.code}` : ''}`;
  const eyebrow =
    effectiveCountry && effectiveCountry.code !== 'SG'
      ? `Services // ${content.keyword} — ${effectiveCountry.name}`
      : `Services // ${content.keyword}`;

  return (
    <div data-testid={`category-page-${cat.slug}${effectiveCountry ? `-${effectiveCountry.code.toLowerCase()}` : ''}`}>
      <PageHero
        eyebrow={eyebrow}
        lines={
          effectiveCountry
            ? [cat.name + '.', <span key="l2">In <span className="text-volt">{effectiveCountry.seoName}.</span></span>]
            : [cat.name + '.', <span key="l2">Across <span className="text-volt">10 markets.</span></span>]
        }
        sub={content.tagline}
      >
        <Reveal delay={0.75} className="mt-10">
          <Link
            to={rfqTo}
            data-testid="category-hero-cta"
            className="group inline-flex items-center gap-2 bg-volt px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink transition-transform duration-200 hover:-translate-y-0.5 hover:bg-voltdark"
          >
            Post a {cat.name} RFQ
            <ArrowUpRight size={15} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </PageHero>

      <section className="border-b border-line bg-ink">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 py-24 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-7">
            <Chapter no="01" label={content.keyword} title="The work, and who does it." />
            <div className="mt-10 space-y-6">
              {content.intro.map((p, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <p className="text-base leading-relaxed text-steel">{p}</p>
                </Reveal>
              ))}
            </div>
            {content.sections.map((s, i) => (
              <div key={s.h} className="mt-14">
                <Reveal>
                  <h3 className="font-display text-2xl font-bold tracking-tight text-parchment">{s.h}</h3>
                </Reveal>
                <div className="mt-5 space-y-5">
                  {s.paragraphs.map((p, j) => (
                    <Reveal key={j} delay={j * 0.06}>
                      <p className="text-base leading-relaxed text-steel">{p}</p>
                    </Reveal>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="lg:col-span-5">
            <Reveal className="lg:sticky lg:top-24">
              <div className="duotone clip-frame aspect-[4/3] border border-line" data-testid="category-image">
                <img src={cat.image} alt={`${cat.name} — ${content.keyword}`} className="h-full w-full object-cover" loading="lazy" />
              </div>
              <div className="mt-6 border border-line bg-surface p-8">
                <p className="mono-label text-volt">{content.servicesTitle}</p>
                <ul className="mt-5 space-y-3">
                  {content.services.map((s) => (
                    <li key={s} className="flex items-start gap-2.5 text-sm text-steel">
                      <Check size={15} weight="bold" className="mt-0.5 shrink-0 text-volt" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {effectiveCountry && (
        <section className="border-b border-line bg-surface" data-testid="country-profile-block">
          <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
            <Chapter no="02" label={`${cat.name} — ${effectiveCountry.name}`} title={`The ${effectiveCountry.name} landscape.`} />
            <Reveal>
              <p className="mt-8 max-w-3xl text-base leading-relaxed text-steel">{effectiveCountry.blurb}</p>
            </Reveal>
            <Reveal delay={0.08}>
              <p className="mono-label mt-10 text-volt">Industrial hubs we cover</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {effectiveCountry.hubs.map((h) => (
                  <span
                    key={h}
                    data-testid={`hub-chip-${h.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-steel"
                  >
                    <MapPin size={12} weight="bold" className="text-volt" /> {h}
                  </span>
                ))}
              </div>
            </Reveal>
            {effectiveCountry.code !== 'SG' && (
              <Reveal delay={0.12}>
                <p className="mt-10 max-w-3xl border-l-2 border-volt pl-5 text-sm leading-relaxed text-steel">
                  We are still onboarding {cat.name.toLowerCase()} providers in {effectiveCountry.seoName}. Post your RFQ
                  anyway — during the concierge phase the founder sources and verifies matched providers by hand,
                  and tells you straight if your trade needs more sourcing time.
                </p>
              </Reveal>
            )}
          </div>
        </section>
      )}

      <section className="border-b border-line bg-volt text-ink">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-6 py-16 sm:flex-row sm:items-center sm:justify-between lg:px-10">
          <div>
            <p className="mono-label text-ink/70">Ready when you are</p>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight sm:text-4xl">
              Get matched {cat.name.toLowerCase()} quotes{effectiveCountry ? ` in ${effectiveCountry.seoName}` : ''}.
            </h2>
          </div>
          <Link
            to={rfqTo}
            data-testid="category-bottom-cta"
            className="group flex shrink-0 items-center gap-2 bg-ink px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-volt transition-transform duration-200 hover:-translate-y-0.5"
          >
            Post an RFQ — free
            <ArrowUpRight size={15} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </section>

      {hasCountryPages && (
        <section className="border-b border-line bg-ink">
          <div className="mx-auto max-w-7xl px-6 py-12 lg:px-10">
            <p className="mono-label text-volt">{effectiveCountry ? `${cat.name} in other markets` : `${cat.name} by market`}</p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="category-country-strip">
              {effectiveCountry && (
                <Link to={`/services/${cat.slug}`} data-testid="category-country-link-global" className={chipCls}>
                  All markets
                </Link>
              )}
              {COUNTRIES.filter((c) => !effectiveCountry || c.code !== effectiveCountry.code).map((c) => (
                <Link
                  key={c.code}
                  to={`/services/${cat.slug}/${c.code.toLowerCase()}`}
                  data-testid={`category-country-link-${c.code.toLowerCase()}`}
                  className={chipCls}
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-ink">
        <div className="mx-auto max-w-7xl px-6 py-14 lg:px-10">
          <Link to={nextTo} data-testid="category-next-link" className="group flex items-center justify-between border border-line bg-surface p-8 transition-colors duration-300 hover:border-volt/60">
            <div>
              <p className="mono-label">{cat.vertical ? 'Next focus area' : cat.sector ? 'Next sector' : 'Next category'}</p>
              <p className="mt-2 font-display text-2xl font-bold text-parchment transition-colors duration-200 group-hover:text-volt">
                {next.name}{effectiveCountry && nextMarkets ? ` — ${effectiveCountry.name}` : ''}
              </p>
            </div>
            <ArrowUpRight size={28} className="text-steel transition-all duration-200 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-volt" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CategoryPage;
